// ABOUTME: Result part of the bulk change's third step: apply progress with cancel, then the applied/conflict/skipped outcome.
// ABOUTME: Lists the rows per outcome with their reason and the approve/publish failures when re-approval was requested.

import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@ds/coolshop';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { MAX_ROWS_PAGE_SIZE, useFindReplaceRows } from '../hooks/useFindReplaceRows';
import { useApplyReasonLabel } from '../hooks/useReasonLabels';
import { ApplyOutcome, FindReplaceRunDto, isDeleteRun } from '../types';
import { ContentTypeLabeler } from '../utils/contentTypeLabels';
import { FilterChip } from './FilterChip';
import { FindReplaceRowsTable, RowColumn } from './FindReplaceRowsTable';
import { JobProgress } from './JobProgress';
import { StepFooter } from './StepFooter';

const NO_COLUMNS: RowColumn[] = [];

type ResultStepProps = {
    run: FindReplaceRunDto;
    hierarchyId: string;
    labeler: ContentTypeLabeler;
    defaultPageSize: number;
    onRunChanged: () => void;
    onCancel: (runId: string) => Promise<void>;
    onClose: () => void;
    /** Opens the apply job; absent when the dialog does not know it. */
    onOpenJob?: () => void;
};

export function ResultStep({
    run,
    hierarchyId,
    labeler,
    defaultPageSize,
    onRunChanged,
    onCancel,
    onClose,
    onOpenJob,
}: ResultStepProps) {
    const { formatMessage } = useIntl();
    const deleteRun = isDeleteRun(run.input);
    const applyReasonLabel = useApplyReasonLabel(deleteRun);
    const [outcome, setOutcome] = useState<ApplyOutcome>('applied');
    const [paging, setPaging] = useState({ page: 0, pageSize: defaultPageSize });
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    // Until the apply job picks the run up it can still read `ready`: treat it as in progress.
    const inProgress = run.status === 'applying' || run.status === 'ready';
    const counts = run.result?.counts ?? run.summary.byOutcome;

    const rowsQuery = useFindReplaceRows(
        inProgress ? null : run.id,
        { outcome },
        { skip: paging.page * paging.pageSize, limit: paging.pageSize },
    );

    const trailingColumns = useMemo<RowColumn[]>(
        () => [
            {
                id: 'applyReason',
                header: formatMessage({
                    id: 'findReplace.table.applyReason',
                    defaultMessage: 'Reason',
                }),
                cell: ({ row }) =>
                    row.original.applyReason ? applyReasonLabel(row.original.applyReason) : '',
            },
        ],
        [formatMessage, applyReasonLabel],
    );

    const handleCancel = async () => {
        setBusy(true);
        setActionError(null);
        try {
            await onCancel(run.id);
        } catch (e) {
            setActionError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
            onRunChanged();
        }
    };

    if (inProgress) {
        return (
            <Stack gap={2} p={3}>
                <Typography variant="body1">
                    {formatMessage({
                        id: 'findReplace.result.applying',
                        defaultMessage: 'Applying the changes…',
                    })}
                </Typography>
                <JobProgress job={run.job} />
                <Alert severity="warning">
                    {formatMessage({
                        id: 'findReplace.result.cancelWarning',
                        defaultMessage:
                            'If you cancel, the values already written stay written: only the remaining ones are left unchanged.',
                    })}
                </Alert>
                {actionError && <Alert severity="error">{actionError}</Alert>}
                <Box>
                    <Button variant="text" onClick={handleCancel} disabled={busy}>
                        {formatMessage({
                            id: 'findReplace.result.cancelApply',
                            defaultMessage: 'Cancel the apply',
                        })}
                    </Button>
                </Box>
            </Stack>
        );
    }

    const approvePublish = run.result?.approvePublish;
    const reapproved = approvePublish
        ? approvePublish.attempted - approvePublish.failures.length
        : 0;

    const outcomeChip = (value: ApplyOutcome, label: string) => (
        <FilterChip
            key={value}
            label={label}
            selected={outcome === value}
            onClick={() => {
                setOutcome(value);
                setPaging(prev => ({ ...prev, page: 0 }));
            }}
            data-testid={`bulk-change-outcome-${value}`}
        />
    );

    return (
        <Stack flex={1} minHeight={0}>
            <Stack gap={2} p={3} flex={1} minHeight={0} overflow="auto">
                <Alert
                    severity={
                        counts.applied === 0 && (counts.conflict || counts.skipped)
                            ? 'warning'
                            : 'success'
                    }
                    data-testid="find-replace-result-summary"
                    data-applied={counts.applied}
                    data-conflict={counts.conflict}
                    data-skipped={counts.skipped}
                >
                    <AlertTitle>
                        {deleteRun
                            ? formatMessage(
                                  {
                                      id: 'bulkChange.result.deleted',
                                      defaultMessage:
                                          '{nodes, plural, one {Deleted # object} other {Deleted # objects}}',
                                  },
                                  { nodes: run.appliedNodeCount },
                              )
                            : formatMessage(
                                  {
                                      id: 'bulkChange.result.applied',
                                      defaultMessage:
                                          'Applied {applied, plural, one {# change} other {# changes}} in {nodes, plural, one {# object} other {# objects}}',
                                  },
                                  { applied: counts.applied, nodes: run.appliedNodeCount },
                              )}
                    </AlertTitle>
                    {[
                        approvePublish
                            ? formatMessage(
                                  {
                                      id: 'bulkChange.result.reapproved',
                                      defaultMessage:
                                          'Re-approved and published {count, plural, one {# group} other {# groups}}.',
                                  },
                                  { count: reapproved },
                              )
                            : null,
                        (counts.conflict || counts.skipped) && deleteRun
                            ? formatMessage(
                                  {
                                      id: 'bulkChange.result.notDeleted',
                                      defaultMessage:
                                          '{conflict, plural, one {# object} other {# objects}} no longer existed and {skipped, plural, one {# object} other {# objects}} could not be deleted (the Skipped tab gives the reason).',
                                  },
                                  { conflict: counts.conflict, skipped: counts.skipped },
                              )
                            : null,
                        (counts.conflict || counts.skipped) && !deleteRun
                            ? formatMessage(
                                  {
                                      id: 'bulkChange.result.notWritten',
                                      defaultMessage:
                                          '{conflict, plural, one {# value} other {# values}} changed after the scan and {skipped} {skipped, plural, one {was} other {were}} not written (the Skipped tab gives the reason): they were left as they are.',
                                  },
                                  { conflict: counts.conflict, skipped: counts.skipped },
                              )
                            : null,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                </Alert>

                {run.result?.cancelled && (
                    <Alert severity="info">
                        {deleteRun
                            ? formatMessage({
                                  id: 'bulkChange.result.cancelledDelete',
                                  defaultMessage:
                                      'The apply was cancelled: the counts cover only the objects deleted before the cancel.',
                              })
                            : formatMessage({
                                  id: 'findReplace.result.cancelled',
                                  defaultMessage:
                                      'The apply was cancelled: the counts cover only the values written before the cancel.',
                              })}
                    </Alert>
                )}

                {approvePublish && approvePublish.failures.length > 0 && (
                    <Alert severity="warning">
                        <Stack gap={0.5}>
                            <span>
                                {formatMessage(
                                    {
                                        id: 'bulkChange.result.approvePublishFailed',
                                        defaultMessage:
                                            '{failed, plural, one {# group} other {# groups}} could not be re-approved and published:',
                                    },
                                    { failed: approvePublish.failures.length },
                                )}
                            </span>
                            {approvePublish.failures.map(failure => (
                                <span key={`${failure.nodeId}-${failure.ctg}`}>
                                    {`${failure.nodeCode} › ${labeler.groupLabel(failure.ctg)}: ${
                                        failure.message
                                    }`}
                                </span>
                            ))}
                        </Stack>
                    </Alert>
                )}

                <Stack direction="row" gap={1}>
                    {outcomeChip(
                        'applied',
                        formatMessage(
                            {
                                id: 'findReplace.result.tab.applied',
                                defaultMessage: 'Applied ({count})',
                            },
                            { count: counts.applied },
                        ),
                    )}
                    {outcomeChip(
                        'conflict',
                        formatMessage(
                            {
                                id: 'findReplace.result.tab.conflict',
                                defaultMessage: 'Conflicts ({count})',
                            },
                            { count: counts.conflict },
                        ),
                    )}
                    {outcomeChip(
                        'skipped',
                        formatMessage(
                            {
                                id: 'findReplace.result.tab.skipped',
                                defaultMessage: 'Skipped ({count})',
                            },
                            { count: counts.skipped },
                        ),
                    )}
                </Stack>

                {rowsQuery.error && <Alert severity="error">{rowsQuery.error.message}</Alert>}

                <Box border={1} borderColor="divider" borderRadius={2} overflow="hidden">
                    <FindReplaceRowsTable
                        id={`find-replace-result-${outcome}`}
                        hierarchyId={hierarchyId}
                        rows={rowsQuery.rows}
                        count={rowsQuery.count}
                        loading={rowsQuery.loading}
                        page={paging.page}
                        pageSize={paging.pageSize}
                        onPageChange={(page, pageSize) =>
                            setPaging({ page, pageSize: Math.min(pageSize, MAX_ROWS_PAGE_SIZE) })
                        }
                        actions={run.input.actions}
                        labeler={labeler}
                        hideInherited
                        // A deleted row may have gone with its parent or another object's cascade.
                        trailingColumns={
                            outcome === 'applied' && !deleteRun ? NO_COLUMNS : trailingColumns
                        }
                        emptyMessage={
                            deleteRun
                                ? formatMessage({
                                      id: 'bulkChange.result.emptyDelete',
                                      defaultMessage: 'No objects with this outcome.',
                                  })
                                : formatMessage({
                                      id: 'findReplace.result.empty',
                                      defaultMessage: 'No values with this outcome.',
                                  })
                        }
                    />
                </Box>

                <Typography variant="caption" color="text.secondary">
                    {deleteRun
                        ? formatMessage({
                              id: 'bulkChange.result.footnoteDelete',
                              defaultMessage:
                                  'Applying again only deletes the objects that were not deleted yet. Conflicts need a new preview.',
                          })
                        : formatMessage({
                              id: 'bulkChange.result.footnote',
                              defaultMessage:
                                  'Applying again only writes the values that were not written yet. Conflicts need a new preview.',
                          })}
                </Typography>
            </Stack>
            <StepFooter>
                {onOpenJob && (
                    <Button variant="text" onClick={onOpenJob}>
                        {formatMessage({
                            id: 'bulkChange.result.openJob',
                            defaultMessage: 'Open the job',
                        })}
                    </Button>
                )}
                <Button variant="contained" color="secondary" onClick={onClose}>
                    {formatMessage({ id: 'findReplace.result.close', defaultMessage: 'Close' })}
                </Button>
            </StepFooter>
        </Stack>
    );
}
